import { useEffect, useMemo, useRef, useState } from 'react';
import { COUNTRIES, countryName, flagUrl } from '../../lib/countries.js';

/**
 * Combobox-style country picker with flag preview. The control closes on
 * outside click and on Escape. Selection is propagated as ISO 3166-1
 * alpha-2 lowercase string.
 *
 * Props: value, onChange(code), disabled, allowEmpty=true, id
 */
export function CountrySelect({ value, onChange, disabled, allowEmpty = true, id, placeholder = 'Select country…' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function onDoc(e) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    if (!query) return COUNTRIES;
    const q = query.toLowerCase();
    return COUNTRIES.filter(
      (c) => c.name.toLowerCase().includes(q) || c.code.includes(q),
    );
  }, [query]);

  const selected = value ? { code: value, name: countryName(value) } : null;

  function pick(code) {
    onChange?.(code || null);
    setOpen(false);
    setQuery('');
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="field flex items-center gap-2 text-left disabled:cursor-not-allowed disabled:opacity-60"
      >
        {selected ? (
          <>
            <Flag code={selected.code} size={20} />
            <span className="flex-1 truncate text-ink">{selected.name}</span>
            <span className="font-mono text-[11px] uppercase text-mist">{selected.code}</span>
          </>
        ) : (
          <span className="flex-1 text-mist">{placeholder}</span>
        )}
        <i className={`fa-solid fa-chevron-${open ? 'up' : 'down'} text-[11px] text-mist`} aria-hidden="true" />
      </button>

      {open && !disabled && (
        <div className="absolute left-0 right-0 z-30 mt-1 max-h-80 overflow-hidden rounded-2xl border border-line bg-white shadow-lg">
          <div className="border-b border-line p-2">
            <div className="relative">
              <i className="fa-solid fa-magnifying-glass pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-mist" aria-hidden="true" />
              <input
                autoFocus
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                className="w-full rounded-xl border border-line bg-canvas py-2 pl-8 pr-3 text-sm focus:outline-none focus:border-accent focus:shadow-focus"
              />
            </div>
          </div>
          <ul role="listbox" className="max-h-64 overflow-y-auto py-1 scrollbar-thin">
            {allowEmpty && (
              <li>
                <button
                  type="button"
                  onClick={() => pick(null)}
                  className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-canvas"
                >
                  <span className="inline-flex h-5 w-7 items-center justify-center rounded border border-line bg-canvas text-mist">
                    <i className="fa-solid fa-xmark text-[10px]" aria-hidden="true" />
                  </span>
                  <span className="text-mist">No country</span>
                </button>
              </li>
            )}
            {filtered.map((c) => {
              const active = c.code === value;
              return (
                <li key={c.code}>
                  <button
                    type="button"
                    onClick={() => pick(c.code)}
                    className={
                      'flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors ' +
                      (active ? 'bg-accent-soft text-accent' : 'hover:bg-canvas text-ink')
                    }
                  >
                    <Flag code={c.code} size={20} />
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className="font-mono text-[11px] uppercase text-mist">{c.code}</span>
                    {active && <i className="fa-solid fa-check text-[11px]" aria-hidden="true" />}
                  </button>
                </li>
              );
            })}
            {filtered.length === 0 && (
              <li className="px-3 py-4 text-center text-xs text-mist">No matches</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Standalone flag component. Falls back to a placeholder chip if the
 * code is unknown / SVG missing.
 */
export function Flag({ code, size = 16, className = '' }) {
  if (!code) {
    return (
      <span
        className={`inline-flex items-center justify-center rounded border border-line bg-canvas text-[9px] text-mist ${className}`}
        style={{ width: Math.round(size * 1.4), height: size }}
        aria-hidden="true"
      >
        —
      </span>
    );
  }
  return (
    <img
      src={flagUrl(code)}
      alt=""
      width={Math.round(size * 1.4)}
      height={size}
      className={`inline-block shrink-0 rounded-[2px] border border-line object-cover ${className}`}
      style={{ width: Math.round(size * 1.4), height: size }}
    />
  );
}
